import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from "react-helmet-async";

import Card from './components/Card';
import scienceQuestions from './questions/science.json';
import entertainmentQuestions from './questions/entertainment.json';
import geographyQuestions from './questions/geography.json';
import historyQuestions from './questions/history.json';
import artQuestions from './questions/art.json';
import sportsQuestions from './questions/sports.json';

import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-cards';
import './App.css';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { FaCog, FaArrowLeft } from 'react-icons/fa';

const questions = [
  ...scienceQuestions,
  ...entertainmentQuestions,
  ...geographyQuestions,
  ...historyQuestions,
  ...artQuestions,
  ...sportsQuestions
];

function App() {
  const [started, setStarted] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [options, setOptions] = useState({
    'Science and Nature': { enabled: true, difficulty: [1, 5] },
    Entertainment: { enabled: true, difficulty: [1, 5] },
    Geography: { enabled: true, difficulty: [1, 5] },
    History: { enabled: true, difficulty: [1, 5] },
    'Arts and Literature': { enabled: true, difficulty: [1, 5] },
    'Sports and Leisure': { enabled: true, difficulty: [1, 5] },
  });

  const [sliderVisible, setSliderVisible] = useState({});
  const [flippedCards, setFlippedCards] = useState([]);

  useEffect(() => {
    const storedFlippedCards = JSON.parse(localStorage.getItem('flippedCards')) || [];
    setFlippedCards(storedFlippedCards);
  }, []);

  const toggleOption = (option) => {
    setOptions({
      ...options,
      [option]: {
        ...options[option],
        enabled: !options[option].enabled,
      },
    });
  };

  const updateDifficulty = (option, difficultyRange) => {
    setOptions({
      ...options,
      [option]: {
        ...options[option],
        difficulty: difficultyRange,
      },
    });
  };

  const toggleSlider = (category) => {
    setSliderVisible((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleCardFlip = (id) => {
    if (!flippedCards.includes(id)) {
      const updatedFlippedCards = [...flippedCards, id];
      setFlippedCards(updatedFlippedCards);
      localStorage.setItem('flippedCards', JSON.stringify(updatedFlippedCards));
    }
  };

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };
  
  const startNewGame = () => {
    const newQuestions = getFilteredQuestions()
      .filter(q => !flippedCards.includes(q.id));
    
    setFilteredQuestions(shuffleArray(newQuestions)); // Shuffle only on game start
    setStarted(true);
  };
    

  const getFilteredQuestions = useCallback(() => {
    return questions.filter((question) => {
      const category = question.category;
      const difficulty = question.difficulty;
      return (
        options[category].enabled &&
        difficulty >= options[category].difficulty[0] &&
        difficulty <= options[category].difficulty[1]
      );
    });
  }, [options]); // Now it's memoized and only updates when `options` change
  
  const filteredQuestionsMemo = useMemo(() => getFilteredQuestions(), [getFilteredQuestions]);

  
  const [filteredQuestions, setFilteredQuestions] = useState(filteredQuestionsMemo);

  useEffect(() => {

      setTimeout(() => {
        document.querySelector('.swiper')?.swiper?.slideTo(0, 0); // Ensure it starts at the first slide
        document.querySelector('.swiper')?.swiper?.update(); // Force Swiper to refresh its virtual slides
      }, 50);
    
    if (started) return; // Don't reset if the game is already running
    setStarted(false);


  }, [options, started]);

  const categories = [
    { id: 1, title: 'Science and Nature', className: 'science' },
    { id: 2, title: 'Entertainment', className: 'entertainment' },
    { id: 3, title: 'Geography', className: 'geography' },
    { id: 4, title: 'History', className: 'history' },
    { id: 5, title: 'Arts and Literature', className: 'art' },
    { id: 6, title: 'Sports and Leisure', className: 'sports' },
  ];

  return (
    <>
      <Helmet>
          {/* Basic SEO */}
        <title>Trivia Decks - Free Trivia Questions & Custom Categories</title>
        <meta name="description" content="Enjoy free trivia questions with customizable categories. Perfect for trivia lovers and games with friends!" />
        <meta name="keywords" content="trivia questions, free trivia games, fun quiz games, play trivia with friends, custom trivia categories" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://triviadecks.com" />

        {/* Open Graph (Facebook, LinkedIn, etc.) */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Trivia Decks - Free Trivia Questions & Custom Categories" />
        <meta property="og:description" content="Challenge yourself with fun trivia questions! Play with friends and customize your categories." />
        <meta property="og:url" content="https://triviadecks.com" />

        {/* Twitter Card (For Twitter sharing) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Trivia Decks - Free Trivia Questions & Custom Categories" />
        <meta name="twitter:description" content="Enjoy endless trivia with customizable categories. Play solo or with friends!" />

        {/* Favicon */}
        <link rel="icon" href="https://triviadecks.com/favicon.ico" type="image/x-icon" />
      </Helmet>
      <div className='App'>
        {!started && (
          <div className='menu'>
            <h1>Trivia Decks</h1>
            <button onClick={startNewGame}>Start</button>
            <button onClick={() => setCustomizing(!customizing)}>Customize</button>
            <div className={`accordion-wrapper ${customizing ? 'open' : ''}`}>
              <div className='customization-section'>
                {categories.map((category) => (
                  <div className='customization-option' key={category.id}>
                    <div className={`checkbox-container ${category.className}`}>
                      <input
                        type='checkbox'
                        checked={options[category.title].enabled}
                        onChange={() => toggleOption(category.title)}
                      />
                      <label className={category.title}>{category.title}</label>
                      <FaCog
                        className='cog-icon'
                        onClick={() => toggleSlider(category.className)}
                      />
                    </div>
                    <div
                      className={`slider-dropdown ${
                        sliderVisible[category.className] ? 'visible' : ''
                      }`}
                    >
                      <span className='difficulty'>DIFFICULTY</span>
                      <RangeSlider
                        min={1}
                        max={5}
                        step={1}
                        defaultValue={options[category.title].difficulty}
                        onInput={(value) => updateDifficulty(category.title, value)}
                      />
                      <div className='slider-labels'>
                        {[1, 2, 3, 4, 5].map((number) => (
                          <span key={number}>{number}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {started && (
          <div class="deck-screen">
            <h1>Trivia Decks</h1>

            <Swiper
              effect={'cards'}
              grabCursor={true}
              modules={[EffectCards, Virtual]}
              cardsEffect={{ slideShadows: false }}
              virtual={{
                enabled: true,
                addSlidesBefore: 3,
                addSlidesAfter: 3,
              }}
            >
              {filteredQuestions.map((question, idx) => (
                  <SwiperSlide key={question.id} virtualIndex={idx}>
                  <Card {...question} onCardFlip={handleCardFlip} />
                </SwiperSlide>
              ))}
            </Swiper>
            <button onClick={() => setStarted(false)} className='back-button'>
              <FaArrowLeft className='arrow-icon' />
              Back
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
